import React, { useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import SocialButtons from '../../SocialButtons'
import TextInput from 'components/Common/Form/Input/TextInput'
import MessageBanner from 'components/Luxkit/Banners/MessageBanner'
import PaneBody from 'components/Common/Pane/PaneBody'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import { formToObject } from 'lib/formToObject'
import { checkEmail } from 'actions/AuthActions'
import TextButton from 'components/Luxkit/Button/TextButton'
import ReCAPTCHA from 'react-google-recaptcha'
import config from 'constants/config'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { Form, Or } from '../../common'
import TextInputOnly from 'components/Common/Form/Input/TextInputOnly'
import { loginModalOpen } from 'analytics/eventDefinitions'
import { fireNonInteractionEvent } from 'api/googleTagManager'
import LegalText from '../LegalText'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import { rem } from 'polished'
import { useResponsive } from 'hooks/useResponsive'
import { TrustIndicatorsMapper } from './TrustIndicatorsMapper'
import { LoginStartModalVariantContentDetails } from 'hooks/useLoginStartModalExperiment'

const OrText = styled(BodyText)`
  margin: 0 ${rem(16)};
`

const ButtonContainer = styled.div`
  margin-top: ${rem(12)};
`

const Footer = styled(PaneBody)`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.palette.neutral.two};
`

interface LoginForm {
  email: string;
}

interface Props {
  error?: any;
  open: boolean;
  headlessMode?: boolean;
  shouldShowPhoneNumber?: boolean;
  isStoreMode: boolean;
  experimentContent: LoginStartModalVariantContentDetails;
}

function LoginStartExperiment(props: Props) {
  const {
    error,
    open,
    headlessMode,
    isStoreMode,
    experimentContent,
  } = props

  const nativeInputRef = useRef<HTMLInputElement>(null)
  const textInputRef = useRef<TextInputOnly>(null)
  const dispatch = useAppDispatch()
  const reRef = useRef<ReCAPTCHA>(null)

  useEffect(() => {
    if (open) {
      if (!headlessMode) {
        textInputRef.current?.reset()
        nativeInputRef.current?.focus()
      }
      fireNonInteractionEvent(loginModalOpen())
    }
  }, [open, headlessMode])

  const onCheckEmail = async(e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const loginData = formToObject<LoginForm>(e.currentTarget)
    dispatch(checkEmail(loginData.email, reRef?.current ?? undefined))
  }

  const [match] = useResponsive()
  const isMobile = useMemo(() => match.xxs, [match])

  const socialMediaLoginsEnabled = config.FACEBOOK_APP_ID || config.GOOGLE_APP_ID || config.APPLE_APP_ID
  const showSocialMediaButtons = !isStoreMode && socialMediaLoginsEnabled

  const { body, socialMediaLogin, trustIndicators } = experimentContent

  const experimentProps = {
    customGoogleText: socialMediaLogin.text?.customText?.google,
    customFacebookText: socialMediaLogin.text?.customText?.facebook,
    customAppleText: socialMediaLogin.text?.customText?.apple,
    customIcons: socialMediaLogin.customIcons,
    inline: socialMediaLogin.inline,
    squareIcons: socialMediaLogin.squareIcons,
    hideText: socialMediaLogin.text?.hideText,
  }
  const emailInputProps = {
    label: 'Email address',
    id: 'email',
    required: true,
    type: 'email',
    name: 'email',
    autoComplete: 'email',
    placeholder: 'Email',
    requiredErrorMessage: 'Email is required',
    inputRef: nativeInputRef,
    textInputRef,
    disabled: !open,
    ...(!isMobile && { helpText: "We'll check if you have an account with us or if we need to create one for you." }),
  }

  const { hideFacebook } = trustIndicators || {}

  return (
    <VerticalSpacer gap={16}>
      <Form onSubmit={onCheckEmail} name="loginForm">
        <Group direction="vertical" gap={16}>
          {error?.message && <MessageBanner kind="critical" description={error.message} />}
          <Group direction={hideFacebook ? 'vertical-reverse' : 'vertical'} gap={16}>
            <Group direction="vertical" gap={4}>
              {body.map(({ Icon, text }) => (
                <Group key={text} direction="horizontal" gap={8} verticalAlign="center">
                  {Icon}
                  <BodyText variant="medium">{text}</BodyText>
                </Group>
              ))}
            </Group>
            <TrustIndicatorsMapper hideFacebook={!!hideFacebook} />
          </Group>
          {showSocialMediaButtons && socialMediaLogin.position === 'above-email-input' && (
            <>
              <SocialButtons mode="login" renderId="accountAccessModal" {...experimentProps} />
              <Or>
                <OrText colour="neutral-two" variant="medium">or</OrText>
              </Or>
            </>
          )}
          <TextInput {...emailInputProps}/>
        </Group>
        <ButtonContainer>
          <TextButton
            kind="primary"
            size="large"
            type="submit"
            fit="full-width"
          >
            Continue
          </TextButton>
        </ButtonContainer>
      </Form>
      {showSocialMediaButtons && socialMediaLogin.position === 'below-email-input' && (
        <Group direction="vertical" gap={16}>
          <Or>
            <OrText colour="neutral-two" variant="medium">Other ways to sign in</OrText>
          </Or>
          <SocialButtons mode="login" renderId="accountAccessModal" {...experimentProps} />
        </Group>
      )}
      {config.RECAPTCHA_KEY && <ReCAPTCHA size="invisible" sitekey={config.RECAPTCHA_KEY} ref={reRef} />}
      <Footer>
        <LegalText loginStartExperiment />
      </Footer>
    </VerticalSpacer>
  )
}

function mapStateToProps(state: App.State) {
  return {
    error: state.auth.error,
    headlessMode: state.config.headlessMode,
    isStoreMode: state.config.storeMode,
    customHeadings: state.content?.secureHeadingParams?.signup,
  }
}

export default connect(mapStateToProps)(LoginStartExperiment)
